import type { ExpDisplayUpdate, ExpExactEvidence, ExpPoint, GameEvent, GameStateSnapshot, ValidatedObservation } from '@/domain/types'
import { expMatchesRequirement, expRequirementRange, intersectExpRequirementRanges, representativeExpRequirement, type ExpRequirementRange } from './exp-requirement-estimator'

export const EXP_JOURNEY_REVIEW_MS = 120_000

export function exactSessionExpPoint(state: GameStateSnapshot | null): ExpPoint | null {
  if (!state || state.level === null || state.exp.absolute === null || state.exp.ratio === null
    || state.exp.estimated || !Number.isInteger(state.level) || !Number.isSafeInteger(state.exp.absolute)
    || state.exp.absolute < 0 || !expMatchesRequirement(state.exp, state.exp.requiredExp ?? null)) return null
  return { level: state.level, absolute: state.exp.absolute, ratio: state.exp.ratio,
    percentagePrecision: state.exp.percentagePrecision, requiredExp: state.exp.requiredExp ?? null,
    requiredExpApproximate: state.exp.requiredExpApproximate ?? state.exp.requiredExp != null }
}

interface Cycle {
  level: number
  offset: number
  range: ExpRequirementRange | null
  required: number | null
  anchor: ExpPoint
  wrap?: { point: ExpPoint; at: number; required: number | null }
}

interface JourneyReview {
  fromSourceAt: number
  before: ExpPoint
  range: ExpRequirementRange | null
  lastSourceAt: number | null
  samples: number
  correctionFromSourceAt?: number
}

interface TransitionReview {
  level: number
  range: ExpRequirementRange | null
  sourceAt: number
  count: number
}

/** Displayed net earnings are recomputed from fixed segment endpoints, never from admitted gains. */
export class SessionExpCounter {
  private startAbsolute: number | null = null
  private closedGain: number | null = 0
  private cycles: Cycle[] = []
  private review: TransitionReview | null = null
  private minimumSourceAt = -Infinity
  private endSourceAt: number | null = null
  private segmentPending = false
  private closedApproximate = false
  private display: ExpDisplayUpdate = this.empty()
  private journeyReview: JourneyReview | null = null
  private levelEvidence: { level: number; sourceAt: number } | null = null

  reset(state: GameStateSnapshot | null = null, minimumSourceAt = -Infinity): void {
    this.startAbsolute = null
    this.closedGain = 0
    this.cycles = []
    this.review = null
    this.minimumSourceAt = -Infinity
    this.endSourceAt = null
    this.segmentPending = false
    this.closedApproximate = false
    this.display = this.empty()
    this.journeyReview = null
    this.levelEvidence = null
    if (state) this.ingestState(state)
    this.minimumSourceAt = minimumSourceAt
  }

  breakContinuity(at: number): void {
    if (!this.segmentPending) {
      this.closedGain = this.display.status === 'unavailable' ? 0 : this.display.netGain
      this.closedApproximate = this.display.approximate
    }
    this.startAbsolute = null
    this.cycles = []
    this.review = null
    this.segmentPending = true
    this.minimumSourceAt = at
    this.journeyReview = null
    this.levelEvidence = null
    this.display = { ...this.display, journeyTransition: undefined }
  }

  ingestObservation(observation: ValidatedObservation, minimumConfidence: number): void {
    this.prepareJourney(observation.capturedAt)
    if (observation.accepted.level && observation.level.value !== null
      && observation.level.confidence >= minimumConfidence) {
      this.observeLevel(observation.level.value, observation.level.capturedAt, observation.capturedAt)
    }
    const candidates: { sample: ExpExactEvidence; confirmedUpgrade: boolean }[] = []
    const evidence = observation.expExactEvidence
    if (evidence) candidates.push({ sample: evidence,
      confirmedUpgrade: observation.expEvidenceAdmission?.transition === 'level-up'
        || (!observation.expEvidenceAdmission && observation.expExactEvidenceAccepted === true) })
    const candidate = observation.expExactCandidate
    if (candidate) candidates.push({ sample: { ...candidate, confirmation: 'source-frame' },
      confirmedUpgrade: observation.expCandidateAdmission?.transition === 'level-up' })
    candidates.sort((a, b) => a.sample.sourceAt - b.sample.sourceAt)
    const before = this.endSourceAt
    for (const { sample, confirmedUpgrade } of candidates) this.observe(sample, minimumConfidence, confirmedUpgrade)
    this.updateJourneyReview()
    if (before === this.endSourceAt && observation.capturedAt > (this.endSourceAt ?? -Infinity)) {
      this.display = { ...this.display, status: this.display.status === 'ready' ? 'stale' : this.display.status }
    }
  }

  ingestState(state: GameStateSnapshot): void {
    this.prepareJourney(state.capturedAt)
    if (state.level !== null) this.observeLevel(state.level, state.observedAt?.level ?? state.capturedAt, state.capturedAt)
    const point = exactSessionExpPoint(state)
    if (!point) return
    this.observe({ level: point.level, value: state.exp, sourceAt: state.observedAt?.exp ?? state.capturedAt,
      deliveredAt: state.capturedAt, confidence: state.confidence.exp, percentagePrecision: state.exp.percentagePrecision,
      confirmation: 'consensus' }, .62, true)
  }

  snapshot(): ExpDisplayUpdate { return this.display }
  total(): number | null { return this.display.netGain }
  approximate(): boolean { return this.display.approximate }
  latestSourceAt(): number | null { return this.endSourceAt }

  confirmLevelUp(events: readonly GameEvent[]): void {
    const cycle = this.cycles.at(-1)
    const point = this.display.point
    const event = events.find(event => event.type === 'level-up' && event.from === cycle?.level && event.to === event.from! + 1)
    if (!cycle || !point || !event || cycle.required === null || !cycle.wrap
      || event.capturedAt < cycle.wrap.at || event.capturedAt - cycle.wrap.at > EXP_JOURNEY_REVIEW_MS
      || this.display.netGain === null || !this.levelIsFresh(event.to!, cycle.wrap.at)) return
    const required = cycle.wrap.required ?? cycle.required
    this.cycles.push({ level: event.to!, offset: cycle.offset + required, range: null, required: null,
      anchor: { ...point, level: event.to! } })
    this.display = { ...this.display, point: { ...point, level: event.to! }, netGain: this.display.netGain + required,
      approximate: true, status: 'ready', levelUp: { from: event.from!, to: event.to! } }
    this.review = null
    this.updateJourneyReview()
  }

  private observe(sample: ExpExactEvidence, minimumConfidence: number, confirmedUpgrade: boolean): void {
    const { absolute, ratio } = sample.value
    const range = expRequirementRange(sample.value, sample.percentagePrecision)
    const currentCycle = this.cycles.at(-1)
    const wrap = currentCycle?.wrap
    if (wrap && sample.level === currentCycle.level + 1 && sample.sourceAt - wrap.at <= EXP_JOURNEY_REVIEW_MS
      && this.levelIsFresh(sample.level, this.journeyReview?.fromSourceAt ?? wrap.at)
      && ((absolute === 0 && ratio === 0) || (range != null && !this.compatible(currentCycle.range, range)))) {
      confirmedUpgrade = true
    }
    if (sample.level === null || !Number.isInteger(sample.level) || sample.level < 1 || sample.level > 200
      || absolute === null || !Number.isSafeInteger(absolute) || absolute < 0 || ratio === null
      || !Number.isFinite(ratio) || ratio < 0 || ratio > 1 || sample.value.estimated || range === undefined
      || !Number.isFinite(sample.confidence) || sample.confidence < minimumConfidence
      || !Number.isFinite(sample.sourceAt) || !Number.isFinite(sample.deliveredAt)
      || sample.sourceAt > sample.deliveredAt || sample.sourceAt < this.minimumSourceAt
      || sample.sourceAt < (this.endSourceAt ?? -Infinity)
      || (sample.sourceAt === this.endSourceAt && (!confirmedUpgrade || sample.level === this.display.point?.level))) return

    const required = range ? representativeExpRequirement(range, absolute, sample.value.requiredExp) : null
    const point: ExpPoint = { level: sample.level, absolute, ratio, requiredExp: required,
      requiredExpApproximate: required !== null, percentagePrecision: sample.percentagePrecision ?? 2 }
    const before = this.display.point
    if (this.startAbsolute !== null && !this.journeyReview && before && (point.absolute < before.absolute || point.ratio < before.ratio - .0002
      || point.level !== before.level)) this.beginJourneyReview(before, sample.sourceAt)
    const journeyReview = this.journeyReview
    if (journeyReview && journeyReview.correctionFromSourceAt === undefined
      && (point.absolute < journeyReview.before.absolute || point.ratio < journeyReview.before.ratio - .0002
        || (point.level !== journeyReview.before.level && range != null
          && !this.compatible(expRequirementRange(journeyReview.before) ?? null, range)))) {
      journeyReview.correctionFromSourceAt = sample.sourceAt
      journeyReview.fromSourceAt = Math.min(journeyReview.fromSourceAt, sample.sourceAt)
    }
    this.endSourceAt = sample.sourceAt
    let continuity: ExpDisplayUpdate['continuity'] = 'contiguous'
    let levelUp: ExpDisplayUpdate['levelUp']
    if (this.startAbsolute === null) {
      this.startAbsolute = absolute
      this.cycles = [{ level: point.level, offset: 0, range, required, anchor: point }]
      continuity = this.segmentPending ? 'discontinuous' : 'baseline'
      this.segmentPending = false
    } else {
      let cycle = this.cycles.at(-1)!
      const compatible = this.compatible(cycle.range, range)
      // A corrected level/requirement can invalidate an earlier inferred upgrade.
      let earlierIndex = -1
      for (let index = this.cycles.length - 2; index >= 0; index--) {
        const entry = this.cycles[index]!
        if (entry.range && range && this.compatible(entry.range, range)
          && (point.level === entry.level || (cycle.range !== null && !compatible))) {
          earlierIndex = index
          break
        }
      }
      if (earlierIndex >= 0) {
        if (!this.confirmTransition(point.level, range, sample.sourceAt)) return this.unresolved(point)
        this.cycles = this.cycles.slice(0, earlierIndex + 1)
        cycle = this.cycles.at(-1)!
        cycle.level = point.level
      } else if (point.level !== cycle.level) {
        const anchor = cycle.wrap && sample.sourceAt - cycle.wrap.at <= EXP_JOURNEY_REVIEW_MS ? cycle.wrap.point : cycle.anchor
        const wrapped = absolute < anchor.absolute || ratio < anchor.ratio - .0002
        if (compatible && !(confirmedUpgrade && point.level === cycle.level + 1 && wrapped)) {
          cycle.level = point.level
        } else {
          const sequential = point.level === cycle.level + 1
          const changedRequirement = range !== null && cycle.range !== null && !compatible
          const confirmed = this.levelIsFresh(point.level, this.journeyReview?.fromSourceAt ?? sample.sourceAt)
            && (sequential && wrapped && confirmedUpgrade || this.confirmTransition(point.level, range, sample.sourceAt))
          if (!sequential || cycle.required === null || !confirmed || (!wrapped && !changedRequirement)) {
            return this.unresolved(point)
          }
          levelUp = { from: cycle.level, to: point.level }
          cycle = { level: point.level, offset: cycle.offset + cycle.required, range, required, anchor: point }
          this.cycles.push(cycle)
        }
      }
      this.review = null
      if (point.level === cycle.level && (point.absolute < cycle.anchor.absolute || point.ratio < cycle.anchor.ratio - .0002)
        && !cycle.wrap) {
        cycle.wrap = { point: cycle.anchor, at: sample.sourceAt, required: cycle.required }
      } else if (cycle.wrap && (sample.sourceAt - cycle.wrap.at > EXP_JOURNEY_REVIEW_MS || point.absolute >= cycle.wrap.point.absolute)) {
        cycle.wrap = undefined
      }
      const merged = cycle.range && range ? intersectExpRequirementRanges([cycle.range, range]) : cycle.range ?? range
      if (merged !== undefined) {
        cycle.range = merged
        cycle.required = merged ? representativeExpRequirement(merged, absolute, cycle.required) : cycle.required
        cycle.anchor = point
      }
    }
    const cycle = this.cycles.at(-1)!
    this.display = { netGain: this.closedGain === null ? null : this.closedGain + cycle.offset + absolute - this.startAbsolute,
      sourceAt: sample.sourceAt, point, status: this.closedGain === null ? 'unresolved-level' : 'ready',
      approximate: this.closedApproximate || cycle.offset !== 0, continuity, ...(levelUp ? { levelUp } : {}) }
    this.updateJourneyReview(sample.sourceAt)
  }

  private compatible(left: ExpRequirementRange | null, right: ExpRequirementRange | null): boolean {
    return left !== null && right !== null && intersectExpRequirementRanges([left, right]) !== undefined
  }

  private confirmTransition(level: number, range: ExpRequirementRange | null, sourceAt: number): boolean {
    const previous = this.review
    const merged = previous?.range && range ? intersectExpRequirementRanges([previous.range, range]) : range
    const matches = previous && previous.level === level && merged !== undefined
      && sourceAt > previous.sourceAt && sourceAt - previous.sourceAt <= 120_000
    this.review = { level, range: merged ?? range, sourceAt, count: matches ? previous.count + 1 : 1 }
    return this.review.count >= 3
  }

  private unresolved(point: ExpPoint): void {
    this.display = { netGain: null, point, sourceAt: this.endSourceAt, status: 'unresolved-level',
      approximate: this.closedApproximate, continuity: 'discontinuous' }
    this.updateJourneyReview()
  }

  expireJourney(now: number): void {
    const review = this.journeyReview
    if (!review || now - review.fromSourceAt <= EXP_JOURNEY_REVIEW_MS) return
    this.display = { ...this.display, journeyTransition: {
      status: 'unavailable', fromSourceAt: review.fromSourceAt, toSourceAt: this.endSourceAt ?? review.fromSourceAt,
    } }
    this.journeyReview = null
    const cycle = this.cycles.at(-1)
    if (cycle) cycle.wrap = undefined
  }

  private prepareJourney(now: number): void {
    if (this.display.journeyTransition?.status !== 'pending') this.display = { ...this.display, journeyTransition: undefined }
    this.expireJourney(now)
  }

  private observeLevel(level: number, sourceAt: number, deliveredAt: number): void {
    if (!Number.isInteger(level) || !Number.isFinite(sourceAt) || sourceAt < this.minimumSourceAt
      || sourceAt > deliveredAt || sourceAt <= (this.levelEvidence?.sourceAt ?? -Infinity)) return
    this.levelEvidence = { level, sourceAt }
    const point = this.display.point
    if (this.startAbsolute !== null && !this.journeyReview && point && level !== point.level) this.beginJourneyReview(point, sourceAt)
  }

  private levelIsFresh(level: number, since: number): boolean {
    return this.levelEvidence?.level === level && this.levelEvidence.sourceAt >= since
  }

  private beginJourneyReview(before: ExpPoint, sourceAt: number): void {
    this.journeyReview = { fromSourceAt: sourceAt, before, range: null, lastSourceAt: null, samples: 0 }
    this.display = { ...this.display, journeyTransition: {
      status: 'pending', fromSourceAt: sourceAt, toSourceAt: this.endSourceAt ?? sourceAt,
    } }
  }

  private updateJourneyReview(sourceAt?: number): void {
    const review = this.journeyReview
    const { point, netGain, levelUp } = this.display
    if (!review || !point) return
    const at = this.endSourceAt ?? review.fromSourceAt
    const range = expRequirementRange(point, point.percentagePrecision)
    if (sourceAt !== undefined && sourceAt >= review.fromSourceAt && sourceAt > (review.lastSourceAt ?? -Infinity)) {
      if (point.level === review.before.level && point.absolute < review.before.absolute) {
        const merged = review.range && range ? intersectExpRequirementRanges([review.range, range]) : range
        review.samples = merged === undefined ? 1 : review.samples + 1
        review.range = merged ?? range ?? null
      } else {
        review.samples = 0
        review.range = null
      }
      review.lastSourceAt = sourceAt
    }
    let status: 'pending' | 'confirmed' | 'discarded' = 'pending'
    if (at >= review.fromSourceAt && netGain !== null && this.display.status !== 'unresolved-level') {
      if (levelUp || point.level !== review.before.level) status = 'confirmed'
      else if (this.levelEvidence?.level === point.level
        && point.absolute >= review.before.absolute && point.ratio >= review.before.ratio - .0002) status = 'discarded'
      else if (review.samples >= 3 && this.levelIsFresh(point.level, review.fromSourceAt)
        && (range === null || this.compatible(expRequirementRange(review.before) ?? null, range ?? null))) status = 'confirmed'
    }
    this.display = { ...this.display, journeyTransition: {
      status, fromSourceAt: review.fromSourceAt, toSourceAt: at,
      ...(status === 'confirmed' ? { netOffset: netGain! - point.absolute, level: point.level,
        correctionFromSourceAt: levelUp ? review.correctionFromSourceAt ?? review.fromSourceAt : review.fromSourceAt,
        ...(levelUp ? { levelUp } : {}) } : {}),
    } }
    if (status !== 'pending') {
      this.journeyReview = null
      const cycle = this.cycles.at(-1)
      if (cycle) cycle.wrap = undefined
    }
  }

  private empty(): ExpDisplayUpdate {
    return { netGain: null, point: null, sourceAt: null, status: 'unavailable', approximate: false, continuity: 'baseline' }
  }
}
